import type { RecurringRevenueReport } from '@vy/types';
import { useMemo } from 'react';
import { extractMonthlyColumnKeys } from '../utils/recurring-revenue-month-keys';
import { applyRecurringRevenueSearch } from '../utils/apply-recurring-revenue-search';
import {
  aggregateByPeriod,
  buildContractsByCustomer,
  buildCustomerMrrByMonth,
  buildWaterfallForRange,
  classifyMonthlyMovements,
  computeBeginningMrr,
  emptyPoint,
  filterMonthKeysByDateRange,
  formatMonthLabel,
  prepareRows,
  trimEmptyEdges,
} from '../utils/chart-movements';
import type {
  ChartPeriodMode,
  ChartResult,
  ChartViewMode,
} from '../utils/chart-movements';

type UseRecurringRevenueReportChartDataParams = {
  // Pre-filtered report from the worker bundle (filters already applied).
  filteredReport: RecurringRevenueReport;
  fromDate?: Date | null;
  toDate?: Date | null;
  periodMode: ChartPeriodMode;
  viewMode: ChartViewMode;
  searchText?: string;
};

export function useRecurringRevenueReportChartData({
  filteredReport,
  fromDate,
  toDate,
  periodMode,
  viewMode,
  searchText,
}: UseRecurringRevenueReportChartDataParams): ChartResult {
  // Worker already applied filters — only apply the client-side search step.
  const searchFilteredReport = useMemo(
    () => applyRecurringRevenueSearch(filteredReport, searchText),
    [filteredReport, searchText],
  );

  const allMonthKeys = useMemo(() => extractMonthlyColumnKeys(searchFilteredReport), [searchFilteredReport]);

  const preparedRows = useMemo(
    () => prepareRows(searchFilteredReport, allMonthKeys),
    [searchFilteredReport, allMonthKeys],
  );

  const contractsByCustomer = useMemo(
    () => buildContractsByCustomer(preparedRows),
    [preparedRows],
  );

  const customerMrrByMonth = useMemo(
    () => buildCustomerMrrByMonth(preparedRows, allMonthKeys),
    [preparedRows, allMonthKeys],
  );

  const monthlyMovementsByKey = useMemo(
    () => classifyMonthlyMovements(customerMrrByMonth, contractsByCustomer, allMonthKeys),
    [customerMrrByMonth, contractsByCustomer, allMonthKeys],
  );

  const selectedMonthKeys = useMemo(
    () => filterMonthKeysByDateRange(allMonthKeys, fromDate ?? null, toDate ?? null),
    [allMonthKeys, fromDate, toDate],
  );

  const selectedMonthlyPoints = useMemo(
    () =>
      selectedMonthKeys.map(
        (key) => monthlyMovementsByKey.get(key) ?? emptyPoint(formatMonthLabel(key)),
      ),
    [selectedMonthKeys, monthlyMovementsByKey],
  );

  return useMemo<ChartResult>(() => {
    if (viewMode === 'bridge') {
      const beginning = computeBeginningMrr(selectedMonthKeys, customerMrrByMonth, allMonthKeys);
      const label = buildRangeLabel(selectedMonthlyPoints);
      return {
        mode: 'waterfall',
        data: buildWaterfallForRange(selectedMonthlyPoints, beginning, label),
      };
    }

    const aggregated = aggregateByPeriod(selectedMonthlyPoints, periodMode);
    return {
      mode: 'timeseries',
      data: trimEmptyEdges(aggregated),
    };
  }, [
    viewMode,
    selectedMonthlyPoints,
    selectedMonthKeys,
    periodMode,
    customerMrrByMonth,
    allMonthKeys,
  ]);
}

function buildRangeLabel(points: { label: string }[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) return points[0].label;
  return `${points[0].label} – ${points[points.length - 1].label}`;
}

export type { ChartPeriodMode, ChartResult, ChartViewMode } from '../utils/chart-movements';
export type { WaterfallChartData, MrrMovementChartDataPoint } from '../utils/chart-movements';
